import React from "react";
import {
  FileText,
  Clock,
  Truck,
  CheckCircle,
  RotateCcw,
  RefreshCcw,
  Package,
  IndianRupee,
  Settings,
  ShoppingCart,
  ShoppingBag,
} from "lucide-react";
import { INVENTORY_SUB_TABS } from "../../../constants/globalConstants";

import { useInventoryLogic } from "../hooks/useInventoryLogic";
import { Modal, Badge, StatCard, Tabs, Pagination } from "../../../components/ui";
import { fmt } from "../../../lib/formatters";
import api from "../../../services/api";
import { exportBlumStockData } from "../../../utils/exportBlumStock";
import { toast } from "react-hot-toast";
import { InventoryStats } from "../components/InventoryStats";
import { InventoryTabs } from "../components/InventoryTabs";
import { InventoryFormModal } from "../components/InventoryFormModal";
import { RestockModal } from "../components/RestockModal";
import { RestockReturnModal } from "../components/RestockReturnModal";
import { MaterialRequestDetailModal } from "../components/MaterialRequestDetailModal";
import { MaterialReturnDetailModal } from "../components/MaterialReturnDetailModal";
import { PurchaseReturnDetailModal } from "../components/PurchaseReturnDetailModal";
import { DispatchModal } from "../components/DispatchModal";
import { PurchaseReturnPaymentModal } from "../components/PurchaseReturnPaymentModal";
import { PurchaseReturnForm } from "../components/PurchaseReturnForm";
import { SaleReturnModal } from "../components/SaleReturnModal";
import { SaleReturnDetailModal } from "../components/SaleReturnDetailModal";
import { SaleReturnPaymentModal } from "../components/SaleReturnPaymentModal";
import { PurchaseEntryModal } from "../components/PurchaseEntryModal";
import { OpenStockEntryModal } from "../components/OpenStockEntryModal";
import { SaleEntryModal } from "../components/SaleEntryModal";
import { PaymentDetailsModal } from "../components/PaymentDetailsModal";
import { InventorySettings } from "../../verve-inventory-settings/screens/InventorySettingsScreen";
import { RequestMaterialsModal } from "../components/RequestMaterialsModal";

// Modular Views
import { ItemsMasterView } from "../components/ItemsMasterView";
import { PurchasesView } from "../components/PurchasesView";
import { SalesView } from "../components/SalesView";
import { OpenStockView } from "../components/OpenStockView";
import { ReturnsView } from "../components/ReturnsView";
import { RequestsView } from "../components/RequestsView";
import { ItemDetailsModal } from "../components/ItemDetailsContent";



/**
 * InventoryScreen Component
 * Refactored to match standard Venus Solar design patterns.
 * Clean sub-tabs, standardized page layout, and premium components.
 */
export const InventoryScreen = () => {
  const [viewPaymentData, setViewPaymentData] = React.useState(null);
  const {
    user,
    inventory,
    activeInventory,
    materialRequests,
    damagedItems,
    filteredRequests,
    projects,
    activeTab,
    purchases,
    sales,
    inventorySubTab,
    setInventorySubTab,
    viewMode,
    setViewMode,
    blumTab,
    setBlumTab,
    search,
    setSearch,
    filters,
    setFilters,
    lowStock,
    noStockCount,
    filtered,
    totalValue,
    itemCount,
    totalUnits,
    averageValuePerItem,
    statsBreakdown,
    requestStats,
    activeReturnStats,
    activeReturnFilterOptions,
    purchaseStats,
    saleStats,
    openStockStats,
    isExpanded,
    setIsExpanded,
    addOpen,
    setAddOpen,
    restockItem,
    setRestockItem,
    restockQty,
    setRestockQty,
    restockReturn,
    setRestockReturn,
    form,
    setForm,
    selReq,
    setSelReq,
    selReturn,
    setSelReturn,
    selSale,
    setSelSale,
    selPR,
    setSelPR,
    selSR,
    setSelSR,
    selectedPRForPayment,
    setSelectedPRForPayment,
    selectedSRForPayment,
    setSelectedSRForPayment,
    initialReturnInvoice,
    setInitialReturnInvoice,
    dispatchReq,
    setDispatchReq,
    addItem,
    addSale,
    handleNewPurchase,
    handleNewOpenStock,
    handleEditOpenStock,
    deleteItem,
    handleEdit,
    ownerAction,
    handleDispatch,
    handleDeliver,
    handleConfirmReturn,
    loading,
    viewItem,
    setViewItem,
    handleViewPurchases,
    dynamicSettings,
    editingItem,
    setEditingItem,
    editingOpenStock,
    setEditingOpenStock,
    consolidatedSales,
    purchaseReturnOpen,
    setPurchaseReturnOpen,
    saleReturnOpen,
    setSaleReturnOpen,
    openStockOpen,
    setOpenStockOpen,
    openingStocks,
    returnsSubTab,
    setReturnsSubTab,
    consolidatedReturns,
    allAggregatedItems,
    distributedMap,
    purchasedMap,
    filteredDamagedItems,
    handlePurchaseReturnPayment,
    handleSaleReturnPayment,
    handleSelectExistingItem,
    purchaseOpen,
    setPurchaseOpen,
    filteredPurchases,
    filteredOpenStock,
    saleOpen,
    setSaleOpen,
    finalRestockReturn,
    restock,
    getInventorySubTabs,
    addItemToList,
    removeItemFromList,
    editItemInList,
    refreshAll,
    submitting,
  } = useInventoryLogic();

  const [isRequestModalOpen, setIsRequestModalOpen] = React.useState(false);
  const [requestModalConfig, setRequestModalConfig] = React.useState({
    project: null,
    initialItems: {},
    initialDepartment: "",
  });




  const handleAddMaterialsToRequest = (req) => {
    // Map existing items to the format expected by RequestMaterialsModal
    const initialItems = {};
    req.items.forEach((it) => {
      // Robustly extract the ID string from the inventoryId object or fallback fields
      const invId = it.inventoryId?._id || it.inventoryId?.id || it.inventoryId;
      const itemId = it.item?._id || it.item?.id || it.itemId;
      const id =
        (typeof invId === "string" ? invId : null) ||
        (typeof itemId === "string" ? itemId : null) ||
        it._id;

      if (id && typeof id === "string") {
        initialItems[id] = Number(it.qty || it.quantity || 0);
      }
    });

    setRequestModalConfig({
      project:
        req.projectDetails || projects.find((p) => p._id === req.projectId),
      initialItems,
      initialDepartment: req.installationTrade || "",
      requestId: req._id || req.id,
    });
    setIsRequestModalOpen(true);
  };

  const handleCreateNewRequest = () => {
    setRequestModalConfig({
      project: null,
      initialItems: {},
      initialDepartment: "",
      requestId: null,
    });
    setIsRequestModalOpen(true);
  };

  const renderInventoryContent = () => {
    switch (inventorySubTab) {
      case INVENTORY_SUB_TABS.ITEMS:
        return (
          <ItemsMasterView
            {...{
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              filtered,
              setRestockItem,
              handleEdit,
              handleViewPurchases,
              deleteItem,
              distributedMap,
              purchasedMap,
            }}
          />
        );
      case INVENTORY_SUB_TABS.PURCHASES:
        return (
          <PurchasesView
            {...{
              search,
              setSearch,
              filters,
              setFilters,
              dynamicSettings,
              activeTab,
              activeInventory,
              inventory,
              purchases: filteredPurchases,
              setInventorySubTab,
              handleNewPurchase,
              setViewItem,
              setRestockItem,
              setInitialReturnInvoice,
              setSelPR,
              setPurchaseReturnOpen,
              setEditingItem,
              setForm,
              setAddOpen,
              setPurchaseOpen,
              refreshAll,
            }}
          />
        );
      case INVENTORY_SUB_TABS.SALES:
        return (
          <SalesView
            {...{
              dynamicSettings,
              activeTab,
              allSales: sales,
              setSelReq,
              setSelSale,
              setSaleOpen,
              refreshAll,
            }}
          />
        );
      case INVENTORY_SUB_TABS.OPEN_STOCK:
        return (
          <OpenStockView
            {...{
              dynamicSettings,
              activeTab,
              allOpeningStocks: openingStocks,
              inventory,
              handleNewOpenStock,
              setViewItem,
              handleEditOpenStock,
              deleteItem,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-500">
      <InventoryTabs
        viewMode={viewMode}
        setViewMode={setViewMode}
        activeTab={activeTab}
        user={user}
      />
      <div className="flex flex-col gap-6">
        {/* Brand Hero Banner */}
        <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
          <div className="pl-8 z-20">
            <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">Verve Inventory</h1>
            <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Inventory Management</p>
          </div>
          <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
            <img
              src="/kitchens/1.jpg"
              alt="Inventory Management"
              className="absolute top-0 right-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
        {viewMode === "requests" && (
          <RequestsView
            {...{
              search,
              setSearch,
              filters,
              setFilters,
              filteredRequests,
              projects,
              user,
              setSelReq,
              setDispatchReq,
              handleDeliver,
              ownerAction,
              onAddItems: handleAddMaterialsToRequest,
              onCreateRequest: handleCreateNewRequest,
            }}
          />
        )}

        {/* {viewMode === "blum" && (
          <div className="space-y-6">
            <Tabs 
              tabs={[
                { id: "purchases", label: "Purchases", icon: IndianRupee }, 
                // nithin: Hidden legacy tabs, moving everything into Purchase Master
                // { id: "blum_stock", label: "Blum Prices", icon: Package },
                // { id: "price_list", label: "Price List", icon: FileText },
              ]}
              activeTab={blumTab}
              onChange={(id) => {
                setBlumTab(id);
                setSearch("");
              }}
              variant="main"
              className="!border-b-0"
            />

            <div className="flex flex-col gap-6">
              {blumTab === "purchases" && (
                <div className="space-y-6">
                  <InventoryStats
                    activeTab={activeTab}
                    viewMode="inventory"
                    subTab={INVENTORY_SUB_TABS.PURCHASES}
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                    inventory={allAggregatedItems}
                    purchases={filteredPurchases}
                  />
                  <PurchasesView
                    {...{
                      search,
                      setSearch,
                      filters,
                      setFilters,
                      dynamicSettings,
                      activeTab,
                      activeInventory,
                      inventory,
                      purchases: filteredPurchases,
                      setInventorySubTab,
                      handleNewPurchase,
                      setViewItem,
                      setRestockItem,
                      setInitialReturnInvoice,
                      setSelPR,
                      setPurchaseReturnOpen,
                      setEditingItem,
                      setForm,
                      setAddOpen,
                      setPurchaseOpen,
                      refreshAll,
                    }}
                  />
                </div>
              )}



              {blumTab === "blum_stock" && (
                <div className="space-y-6">
                  <BlumStockStats stats={blumStockStats} />
                  <BlumStockView
                    search={blumStockSearch}
                    setSearch={setBlumStockSearch}
                    filters={blumStockFilters}
                    setFilters={setBlumStockFilters}
                    stockItems={blumStockItems}
                    refreshStock={fetchBlumStock}
                    page={blumStockPage}
                    totalPages={blumStockTotalPages}
                    totalItems={blumStockTotalItems}
                    handlePageChange={handleBlumStockPageChange}
                    onExport={() => exportBlumStockData({ ...blumStockFilters, search: blumStockSearch })}
                    onAddStock={() => {
                      setSelectedBlumStock(null);
                      setIsBlumStockModalOpen(true);
                    }}
                    onViewStock={(stock) => {
                      setViewItem(stock);
                    }}
                    onEditStock={(stock) => {
                      setSelectedBlumStock(stock);
                      setIsBlumStockModalOpen(true);
                    }}
                  />
                  {isBlumStockModalOpen && (
                    <BlumStockModal
                      isOpen={isBlumStockModalOpen}
                      onClose={() => {
                        setIsBlumStockModalOpen(false);
                        setSelectedBlumStock(null);
                      }}
                      initialData={selectedBlumStock}
                      onSave={handleSaveBlumStock}
                    />
                  )}
                </div>
              )}

              {blumTab === "price_list" && (
                <div className="space-y-6">
                  <PriceListView />
                </div>
              )}

              {blumTab === "blum_purchases" && (
                <div className="space-y-6">
                  <BlumPurchasesStats stats={blumStats} />
                  <BlumPurchasesView 
                    search={search}
                    setSearch={setSearch}
                    filters={filters}
                    setFilters={setFilters}
                    purchases={actualBlumPurchases}
                    onAddPurchase={() => {
                      setSelectedBlumPurchase(null);
                      setIsBlumPurchaseModalOpen(true);
                    }}
                    onViewPurchase={(purchase) => {
                      setViewItem(purchase);
                    }}
                    onEditPurchase={(purchase) => {
                      setSelectedBlumPurchase(purchase);
                      setIsBlumPurchaseModalOpen(true);
                    }}
                    onRestock={(purchase) => {
                      setRestockItem(purchase);
                    }}
                    onReturn={(purchase) => {
                      setInitialReturnInvoice(purchase.invoiceNumber || purchase.invoiceNo || "");
                      setSelPR(null);
                      setPurchaseReturnOpen(true);
                    }}
                  />
                  {isBlumPurchaseModalOpen && (
                    <BlumPurchasesAddPurchases
                      isOpen={isBlumPurchaseModalOpen}
                      onClose={() => {
                        setIsBlumPurchaseModalOpen(false);
                        setSelectedBlumPurchase(null);
                      }}
                      initialData={selectedBlumPurchase}
                      dynamicSettings={dynamicSettings}
                      onSave={async (data) => {
                        try {
                           let res;
                           if (selectedBlumPurchase) {
                              res = await api.patch(`/inventory/blum-purchases/${selectedBlumPurchase._id}`, data);
                           } else {
                              res = await api.post('/inventory/blum-purchases', data);
                           }
                           if (res && res.status !== 'error') {
                              toast.success("Blum purchase recorded successfully!");
                              setIsBlumPurchaseModalOpen(false);
                              setSelectedBlumPurchase(null);
                              // Refetch to update table & stats instantly
                              const fetchBlumPurchases = async () => {
                                try {
                                  const r = await api.get('/inventory/blum-purchases');
                                  if (r) {
                                    const arr = r.data?.data || r.data || r.purchases || [];
                                    setBlumPurchases(Array.isArray(arr) ? arr : []);
                                  }
                                } catch (err) {}
                              };
                              fetchBlumPurchases();
                           }
                        } catch (error) {
                           toast.error(error.message || "Failed to save purchase");
                        }
                      }}
                    />
                  )}
                </div>
              )}

              {blumTab === "vendor_purchases" && (
                <div className="space-y-6">
                  <VendorPurchasesStats stats={vendorStats} />
                  <VendorPurchasesView 
                    search={search}
                    setSearch={setSearch}
                    filters={filters}
                    setFilters={setFilters}
                    purchases={vendorPurchasesData}
                    onAddPurchase={() => {
                      setSelectedVendorPurchase(null);
                      setIsVendorPurchaseModalOpen(true);
                    }}
                    onViewPurchase={(purchase) => {
                      setViewItem(purchase);
                    }}
                    onEditPurchase={(purchase) => {
                      setSelectedVendorPurchase(purchase);
                      setIsVendorPurchaseModalOpen(true);
                    }}
                    onRestock={(purchase) => {
                      setRestockItem(purchase);
                    }}
                    onReturn={(purchase) => {
                      setInitialReturnInvoice(purchase.invoiceNumber || purchase.invoiceNo || "");
                      setSelPR(null);
                      setPurchaseReturnOpen(true);
                    }}
                  />
                  {isVendorPurchaseModalOpen && (
                    <VendorPurchasesAddPurchases
                      isOpen={isVendorPurchaseModalOpen}
                      onClose={() => {
                        setIsVendorPurchaseModalOpen(false);
                        setSelectedVendorPurchase(null);
                      }}
                      initialData={selectedVendorPurchase}
                      dynamicSettings={dynamicSettings}
                      onSave={async (data) => {
                         try {
                            let res;
                            if (selectedVendorPurchase) {
                               res = await api.patch(`/inventory/blum-purchases/${selectedVendorPurchase._id}`, data);
                            } else {
                               res = await api.post('/inventory/blum-purchases', data);
                            }
                            if (res && res.status !== 'error') {
                               toast.success("Vendor purchase recorded successfully!");
                               setIsVendorPurchaseModalOpen(false);
                               setSelectedVendorPurchase(null);
                               // Refetch
                               const fetchBlumPurchases = async () => {
                                 try {
                                   const r = await api.get('/inventory/blum-purchases');
                                   if (r) {
                                     const arr = r.data?.data || r.data || r.purchases || [];
                                     setBlumPurchases(Array.isArray(arr) ? arr : []);
                                   }
                                 } catch (err) {}
                               };
                               fetchBlumPurchases();
                            }
                         } catch (error) {
                            toast.error(error.message || "Failed to save purchase");
                         }
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )} */}

        {viewMode === "inventory" && (
          <div className="space-y-6">
            <InventoryStats
              activeTab={activeTab}
              viewMode={viewMode}
              subTab={inventorySubTab}
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
              inventory={allAggregatedItems}
              sales={consolidatedSales}
              returns={consolidatedReturns}
              requests={materialRequests}
              purchaseReturns={[]}
              purchases={filteredPurchases}
              openingStocks={filteredOpenStock}
            />

            <Tabs
              tabs={getInventorySubTabs()}
              activeTab={inventorySubTab}
              onChange={(id) => {
                setInventorySubTab(id);
                setSearch("");
              }}
              variant="sub"
              className="mb-2"
            />

            <div className="min-h-[400px]">{renderInventoryContent()}</div>
          </div>
        )}

        {viewMode === "settings" && (
          <div className="space-y-6">
            <InventorySettings />
          </div>
        )}

        {viewMode === "returns" && (
          <div className="space-y-6">
            <ReturnsView
              {...{
                returnsSubTab,
                setReturnsSubTab,
                search,
                setSearch,
                filters,
                setFilters,
                dynamicSettings,
                activeTab,
                activeReturnFilterOptions,
                consolidatedReturns,
                damagedItems: filteredDamagedItems,
                setSelReturn,
                setSelPR,
                setSelSR,
                setPurchaseReturnOpen,
                setSaleReturnOpen,
                handleConfirmReturn,
                setRestockReturn,
                setViewPaymentData,
                setSelectedPRForPayment,
                setSelectedSRForPayment,
              }}
            />
          </div>
        )}
      </div>
      
      {/* Modals */}
      <OpenStockEntryModal
        isOpen={openStockOpen}
        onClose={() => {
          setOpenStockOpen(false);
          setEditingOpenStock(null);
        }}
        form={form}
        setForm={setForm}
        onAddToList={addItemToList}
        onRemoveFromList={removeItemFromList}
        onEditItem={editItemInList}
        onSelectExistingItem={handleSelectExistingItem}
        onSubmit={addItem}
        dynamicSettings={dynamicSettings}
        inventory={inventory}
        loading={loading}
        refreshAll={refreshAll}
      />
      <RestockModal
        restockItem={restockItem}
        onClose={() => setRestockItem(null)}
        restockQty={restockQty}
        setRestockQty={setRestockQty}
        onRestock={restock}
        submitting={submitting}
      />
      <RestockReturnModal
        restockReturn={restockReturn}
        onClose={() => setRestockReturn(null)}
        onRestock={finalRestockReturn}
      />
      <InventoryFormModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        activeTab={activeTab}
        onSubmit={addItem}
        dynamicSettings={dynamicSettings}
        isEditing={!!editingItem}
        inventory={inventory}
        onItemSelect={handleSelectExistingItem}
        initialData={form}
      />
      <MaterialRequestDetailModal
        selReq={selReq}
        onClose={() => setSelReq(null)}
        projects={projects}
        inventoryMaster={allAggregatedItems}
        user={user}
        onOwnerAction={ownerAction}
        onApproveRequest={setDispatchReq}
        isInventoryModule={true}
      />
      <MaterialReturnDetailModal
        selReturn={selReturn}
        onClose={() => setSelReturn(null)}
        user={user}
        onConfirmReceipt={handleConfirmReturn}
      />
      <PurchaseReturnDetailModal
        purchaseReturn={selPR}
        onClose={() => setSelPR(null)}
      />
      <PaymentDetailsModal
        isOpen={!!viewPaymentData}
        onClose={() => setViewPaymentData(null)}
        data={viewPaymentData}
      />
      <PurchaseReturnPaymentModal
        isOpen={!!selectedPRForPayment}
        onClose={() => setSelectedPRForPayment(null)}
        returnData={selectedPRForPayment}
        onConfirm={handlePurchaseReturnPayment}
      />
      <SaleReturnDetailModal
        saleReturn={selSR}
        onClose={() => setSelSR(null)}
      />
      <SaleReturnPaymentModal
        isOpen={!!selectedSRForPayment}
        onClose={() => setSelectedSRForPayment(null)}
        returnData={selectedSRForPayment}
        onConfirm={handleSaleReturnPayment}
      />
      <DispatchModal
        isOpen={!!dispatchReq}
        onClose={() => setDispatchReq(null)}
        requestId={dispatchReq?._id || dispatchReq?.id || dispatchReq}
        onConfirm={(id, data) => handleDispatch(dispatchReq, data)}
      />

      <SaleReturnModal
        isOpen={saleReturnOpen}
        onClose={() => setSaleReturnOpen(false)}
        onSuccess={refreshAll}
        dynamicSettings={dynamicSettings}
        serviceType={activeTab}
      />

      <PurchaseReturnForm
        isOpen={purchaseReturnOpen}
        onClose={() => setPurchaseReturnOpen(false)}
        initialInvoiceNo={initialReturnInvoice}
        dynamicSettings={dynamicSettings}
        serviceType={activeTab}
        onSuccess={() => {
          refreshAll();
        }}
      />
      <PurchaseEntryModal
        isOpen={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        form={form}
        setForm={setForm}
        onAddToList={addItemToList}
        onRemoveFromList={removeItemFromList}
        onEditItem={editItemInList}
        onSelectExistingItem={handleSelectExistingItem}
        onSubmit={addItem}
        dynamicSettings={dynamicSettings}
        inventory={inventory}
        loading={loading}
        refreshAll={refreshAll}
      />
      
      <SaleEntryModal
        isOpen={saleOpen}
        onClose={() => setSaleOpen(false)}
        onSubmit={addSale}
        inventory={inventory}
        dynamicSettings={dynamicSettings}
        loading={loading}
      />

      {/* View Item Modal */}
      <ItemDetailsModal item={viewItem} onClose={() => setViewItem(null)} />

      {isRequestModalOpen && (
        <RequestMaterialsModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          project={requestModalConfig.project}
          projects={projects}
          initialItems={requestModalConfig.initialItems}
          initialDepartment={requestModalConfig.initialDepartment}
          requestId={requestModalConfig.requestId}
          viewerRole={user?.role}
          source="inventory"
          inventory={allAggregatedItems}
          onSuccess={() => {
            refreshAll();
            setIsRequestModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

